import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';

const EditPartnerModal = ({ show, onHide, partner, onSubmit }) => {
    const [formData, setFormData] = useState({
        partnerName: '',
        registrationNumber: '',
        partnerType: '',
        vatNumber: '',
        contactNumber: '',
        email: '',
        partnerManager: '',
        address: '',
        bankName: '',
        accountNumber: '',
        sortCode: '',
        iban: '',
        swift: '',
        remarks: '',
        logo: null
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);

    const partnerTypes = [
        'Technology Partner',
        'Service Provider',
        'Vendor',
        'Consultant',
        'Contractor',
        'Supplier',
        'Other'
    ];

    useEffect(() => {
        if (partner && show) {
            setFormData({
                partnerName: partner.PartnerName || '',
                registrationNumber: partner.RegistrationNumber || '',
                partnerType: partner.PartnerType || '',
                vatNumber: partner.VatNumber || '',
                contactNumber: partner.ContactNumber || '',
                email: partner.Email || '',
                partnerManager: partner.PartnerManager || '',
                address: partner.Address || '',
                bankName: partner.BankName || '',
                accountNumber: partner.AccountNumber || '',
                sortCode: partner.SortCode || '',
                iban: partner.IBAN || '',
                swift: partner.SWIFT || '',
                remarks: partner.RemarksNote || '',
                UserType: 'Partner',
                logo: null // Reset file input
            });
            setErrors({});
            setLogoPreview(null); // Reset preview on open
        }
    }, [partner, show]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            const file = files[0];
            setFormData(prev => ({
                ...prev,
                [name]: file
            }));
            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setLogoPreview(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setLogoPreview(null);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.partnerName.trim()) {
            newErrors.partnerName = 'Partner name is required';
        }

        if (!formData.registrationNumber.trim()) {
            newErrors.registrationNumber = 'Registration number is required';
        }

        if (!formData.partnerType) {
            newErrors.partnerType = 'Partner type is required';
        }

        if (!formData.contactNumber.trim()) {
            newErrors.contactNumber = 'Contact number is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.partnerManager.trim()) {
            newErrors.partnerManager = 'Partner manager is required';
        }

        if (!formData.address.trim()) {
            newErrors.address = 'Address is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const submitData = new FormData();

            // Map form fields to API payload format
            const fieldMapping = {
                partnerName: 'PartnerName',
                registrationNumber: 'RegistrationNumber',
                partnerType: 'PartnerType',
                vatNumber: 'VatNumber',
                contactNumber: 'ContactNumber',
                email: 'Email',
                partnerManager: 'PartnerManager',
                address: 'Address',
                bankName: 'BankName',
                accountNumber: 'AccountNumber',
                sortCode: 'SortCode',
                iban: 'IBAN',
                swift: 'SWIFT',
                remarks: 'RemarksNote',
                logo: 'logoUpload'
            };

            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== '') {
                    const apiKey = fieldMapping[key] || key;
                    submitData.append(apiKey, formData[key]);
                }
            });

            await onSubmit(submitData);
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setErrors({});
        onHide();
    };

    if (!partner) return null;

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="ri-edit-line me-2"></i>
                    Edit Partner
                </Modal.Title>
            </Modal.Header>
            <form onSubmit={handleSubmit}>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className="row">
                        {/* Basic Information */}
                        <div className="col-12">
                            <h6 className="mb-3 text-primary">Basic Information</h6>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Partner Name *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.partnerName ? 'is-invalid' : ''}`}
                                name="partnerName"
                                value={formData.partnerName}
                                onChange={handleChange}
                                placeholder="Enter partner name"
                            />
                            {errors.partnerName && <div className="invalid-feedback">{errors.partnerName}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Registration Number *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.registrationNumber ? 'is-invalid' : ''}`}
                                name="registrationNumber"
                                value={formData.registrationNumber}
                                onChange={handleChange}
                                placeholder="Enter registration number"
                            />
                            {errors.registrationNumber && <div className="invalid-feedback">{errors.registrationNumber}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Partner Type *</label>
                            <select
                                className={`form-control ${errors.partnerType ? 'is-invalid' : ''}`}
                                name="partnerType"
                                value={formData.partnerType}
                                onChange={handleChange}
                            >
                                <option value="">Select partner type</option>
                                {partnerTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                            {errors.partnerType && <div className="invalid-feedback">{errors.partnerType}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">VAT Number</label>
                            <input
                                type="text"
                                className="form-control"
                                name="vatNumber"
                                value={formData.vatNumber}
                                onChange={handleChange}
                                placeholder="Enter VAT number"
                            />
                        </div>

                        {/* Contact Information */}
                        <div className="col-12 mt-3">
                            <h6 className="mb-3 text-primary">Contact Information</h6>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Contact Number *</label>
                            <input
                                type="tel"
                                className={`form-control ${errors.contactNumber ? 'is-invalid' : ''}`}
                                name="contactNumber"
                                value={formData.contactNumber}
                                onChange={handleChange}
                                placeholder="Enter contact number"
                            />
                            {errors.contactNumber && <div className="invalid-feedback">{errors.contactNumber}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Email *</label>
                            <input
                                type="email"
                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter email address"
                            />
                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">In House Partner Manager *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.partnerManager ? 'is-invalid' : ''}`}
                                name="partnerManager"
                                value={formData.partnerManager}
                                onChange={handleChange}
                                placeholder="Enter partner manager name"
                            />
                            {errors.partnerManager && <div className="invalid-feedback">{errors.partnerManager}</div>}
                        </div>

                        <div className="col-12 mb-3">
                            <label className="form-label">Address *</label>
                            <textarea
                                className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Enter complete address"
                            />
                            {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                        </div>

                        {/* Banking Details */}
                        <div className="col-12 mt-3">
                            <h6 className="mb-3 text-primary">Banking Details</h6>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Bank Name</label>
                            <input
                                type="text"
                                className="form-control"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleChange}
                                placeholder="Enter bank name"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Account Number</label>
                            <input
                                type="text"
                                className="form-control"
                                name="accountNumber"
                                value={formData.accountNumber}
                                onChange={handleChange}
                                placeholder="Enter account number"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Sort Code</label>
                            <input
                                type="text"
                                className="form-control"
                                name="sortCode"
                                value={formData.sortCode}
                                onChange={handleChange}
                                placeholder="Enter sort code"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">IBAN</label>
                            <input
                                type="text"
                                className="form-control"
                                name="iban"
                                value={formData.iban}
                                onChange={handleChange}
                                placeholder="Enter IBAN"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">SWIFT</label>
                            <input
                                type="text"
                                className="form-control"
                                name="swift"
                                value={formData.swift}
                                onChange={handleChange}
                                placeholder="Enter SWIFT code"
                            />
                        </div>

                        {/* Additional Information */}
                        <div className="col-12 mt-3">
                            <h6 className="mb-3 text-primary">Additional Information</h6>
                        </div>

                        <div className="col-12 mb-3">
                            <label className="form-label">Remarks/Notes</label>
                            <textarea
                                className="form-control"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Enter any remarks or notes"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Upload New Logo</label>
                            <input
                                type="file"
                                className="form-control"
                                name="logo"
                                onChange={handleChange}
                                accept="image/*"
                            />
                            <small className="form-text text-muted">
                                Accepted formats: JPG, PNG, GIF
                                <br />
                                Leave empty to keep current logo
                            </small>
                            {/* Show preview if new logo is selected, else show current logo */}
                            {logoPreview ? (
                                <div className="mt-2">
                                    <img
                                        src={logoPreview}
                                        alt="New Logo Preview"
                                        style={{ maxWidth: '120px', maxHeight: '120px', border: '1px solid #ddd', borderRadius: 6 }}
                                    />
                                    <div className="text-muted small">Preview of new logo</div>
                                </div>
                            ) : (partner.Uploadlogo || partner.logoUpload) ? (
                                <div className="mt-2">
                                    <img
                                        src={partner.Uploadlogo || partner.logoUpload}
                                        alt="Current Partner Logo"
                                        style={{ maxWidth: '120px', maxHeight: '120px', border: '1px solid #ddd', borderRadius: 6 }}
                                    />
                                    <div className="text-muted small">Current logo</div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Updating...
                            </>
                        ) : (
                            'Update Partner'
                        )}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default EditPartnerModal;
